import { buildTranslate } from 'locales'

const t = buildTranslate('recovery_ai.agent_introduction')

export const AGENT_NAME_MAX_LENGTH = 40

const EDGE_CHARACTERS =
  '\\s\\u0085\\u200B-\\u200F\\u2060-\\u2064\\u180E\\uFEFF\\u202A-\\u202E\\u2066-\\u2069'
const NO_VISIBLE_CHARACTERS = new RegExp(
  `^[${EDGE_CHARACTERS}\\p{Cf}\\u115F\\u1160\\u3164\\uFFA0\\u2800\\u00AD\\u061C\\u034F\\u17B4\\u17B5\\u180B-\\u180F\\uFE00-\\uFE0F\\u{E0000}-\\u{E007F}\\u{E0100}-\\u{E01EF}\\u2065]*$`,
  'u'
)
const AGENT_NAME_EDGE = new RegExp(
  `^[${EDGE_CHARACTERS}]+|[${EDGE_CHARACTERS}]+$`,
  'gu'
)
const MERCHANT_NAME_EDGE = /^[\0\t\n\v\f\r ]+|[\0\t\n\v\f\r ]+$/g
const CONTROL_CHARACTER = new RegExp(
  '\\p{Cc}|[\\u2028\\u2029\\u202A-\\u202E\\u2066-\\u2069]',
  'u'
)

export type AgentNameError = 'too_long' | 'control_characters'

export const normalizeAgentName = (
  value: string | null | undefined
): string => {
  const trimmed = (value ?? '').replace(AGENT_NAME_EDGE, '')
  return NO_VISIBLE_CHARACTERS.test(trimmed) ? '' : trimmed
}

export const agentNameLength = (value: string): number =>
  Array.from(value).length

export const validateAgentName = (
  value: string | null | undefined
): AgentNameError | null => {
  const name = normalizeAgentName(value)
  if (agentNameLength(name) > AGENT_NAME_MAX_LENGTH) return 'too_long'
  if (CONTROL_CHARACTER.test(name)) return 'control_characters'
  return null
}

type IntroKey =
  | 'intro_named'
  | 'intro_named_generic'
  | 'intro_default'
  | 'intro_default_generic'

// One pass over the template, so a name containing "%{merchant}" stays literal.
const fillIntro = (
  key: IntroKey,
  values: { agent?: string; merchant?: string }
): string =>
  t(key, { agent: '%{agent}', merchant: '%{merchant}' }).replace(
    /%\{(agent|merchant)\}/g,
    (_match, name: 'agent' | 'merchant') => values[name] ?? ''
  )

// Same wording as the backend greeting disclosure sentence.
export const composeAgentIntro = (
  agentName: string | null | undefined,
  merchantName: string | null | undefined
): string => {
  const agent = normalizeAgentName(agentName)
  const merchant = (merchantName ?? '').replace(MERCHANT_NAME_EDGE, '')
  if (agent) {
    return merchant
      ? fillIntro('intro_named', { agent, merchant })
      : fillIntro('intro_named_generic', { agent })
  }
  return merchant
    ? fillIntro('intro_default', { merchant })
    : fillIntro('intro_default_generic', {})
}

const DISCLOSURE_PARAGRAPH = /^Hi! I'm [^\n]*AI assistant\.$/

// The server prepends one disclosure paragraph to the stored body.
export const stripGreetingDisclosure = (greeting: string): string => {
  const end = greeting.indexOf('\n\n')
  const first = end === -1 ? greeting : greeting.slice(0, end)
  if (!DISCLOSURE_PARAGRAPH.test(first)) return greeting
  return end === -1 ? '' : greeting.slice(end + 2)
}
